import { LazyQueryResult } from '@apollo/client';
import { defaultValues as blockDefaultValues, BlockDrawerValuesProps } from '@bechtel/client-blocks';
import { CacheKeys, useAppUser, useModulePermission, useSessionStorage } from '@bechtel/client-common';
import { Carousel } from '@bechtel/client-dashboard-common';
import { drawerValues as defaultValues, DrawerValuesProps } from '@bechtel/client-work-packages';
import { aboveBreakpoint, Theme, useMediaQuery } from '@bechtel/gds';
import {
  CrewMember,
  MilestoneFilterStatusFlag,
  WorkPackageFilterByTerm,
  WorkPackageStats,
  WorkPackageStatsInput,
} from '@bechtel/types';
import { t } from '@lingui/macro';
import { get } from 'lodash';
import { FC, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import { dashBoardStatsMock } from '~/form-data';
import { StatBlockStyled } from '~/styled';
import { CommittedMilestoneStatsMock } from '~/superintendent/form-data';
import { getStatBlockGridData, isWorkPackagePersona } from '~/utilities';
import { WorkpackageList } from '~/workpackage-list';
import { PendingApprovalList } from './pending-approval-list/internals';
import {
  DashBoardLeftSectionStyled,
  FEDashboardContentStyled,
  FEDashboardStatStyled,
  PendingApprovalListContainerStyled,
  SectionListStyled,
  StatBlockGridFEStyled,
  WorkPackageContainerStyled,
} from './styled';

interface FieldEngineerDashboardContentProps {
  crewMember: CrewMember | null | undefined;
  selectedOwnershipType: OwnershipType;
  result: LazyQueryResult<{ workPackageStats: WorkPackageStats }, { input: WorkPackageStatsInput }>;
}

export const FieldEngineerDashboardContent: FC<FieldEngineerDashboardContentProps> = ({
  crewMember,
  result,
  selectedOwnershipType,
}) => {
  const { canViewBlockModule, canViewWorkPackageModule } = useModulePermission();
  const navigate = useNavigate();
  const { user } = useAppUser();
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isWideScreen = useMediaQuery(aboveBreakpoint(breakpoints.widescreen));

  const setDrawerValues = useSessionStorage<DrawerValuesProps>(CacheKeys.WorkPackageDrawerValues)[1];
  const setCursor = useSessionStorage<number>(CacheKeys.WorkPackageListCursor)[1];
  const setBlockDrawerValues = useSessionStorage<BlockDrawerValuesProps>(CacheKeys.BlockDrawerValues)[1];
  const { called, loading, data } = result;

  const fieldEngineerDashboardStats =
    crewMember && crewMember.persona && isWorkPackagePersona(crewMember?.persona)
      ? get(data, 'workPackageStats', dashBoardStatsMock)
      : dashBoardStatsMock;
  const {
    numWorkPackages,
    numBlocks,
    numRejects,
    plannedMilestones,
    committedMilestones,
    earnedMilestones,
    approvedMilestones,
  } = fieldEngineerDashboardStats;
  const plannedMilestoneStats = getStatBlockGridData(
    plannedMilestones,
    committedMilestones ? committedMilestones : CommittedMilestoneStatsMock,
    user
  );
  const claimedMilestoneStats = getStatBlockGridData(earnedMilestones, undefined, user);
  const ApprovedMilestoneStats = getStatBlockGridData(approvedMilestones, undefined, user);
  const statsLoading =
    (!called && crewMember && crewMember.persona && isWorkPackagePersona(crewMember?.persona)) || loading;

  const navigateToWorkPackage = async (values: DrawerValuesProps) => {
    if (user && user.crewMember && !isWorkPackagePersona(user.crewMember.persona)) {
      //TODO: Enable filter by owners in drawer
      // const filterByOwners = {
      //   persona: crewMember.persona,
      //   crewMemberId: crewMember.id,
      // };
      values = { ...values, searchScope: false };
    }
    setCursor(1);
    await setDrawerValues(values);
    navigate('work-packages');
  };

  return (
    <FEDashboardContentStyled>
      <FEDashboardStatStyled>
        <StatBlockStyled
          value={String(numWorkPackages)}
          label={t`Work Packages`}
          icon="briefcase"
          style={{ cursor: canViewWorkPackageModule && numWorkPackages > 0 ? `pointer` : `default` }}
          onClick={async () => {
            if (canViewWorkPackageModule && numWorkPackages > 0) {
              navigateToWorkPackage({ ...defaultValues, filterBy: WorkPackageFilterByTerm.ALL });
            }
          }}
          loading={statsLoading}
        />
        <StatBlockStyled
          value={String(numBlocks)}
          label={t`Blocks`}
          icon="exclamation-triangle"
          style={{ cursor: canViewBlockModule && numBlocks > 0 ? `pointer` : `default` }}
          onClick={async () => {
            if (canViewBlockModule && numBlocks > 0) {
              await setBlockDrawerValues(blockDefaultValues);
              navigate('blocks');
            }
          }}
          loading={statsLoading}
        />
        <StatBlockStyled
          value={String(numRejects)}
          label={t`Rejections`}
          icon="comment-alt-times"
          onClick={() => {
            navigateToWorkPackage({ ...defaultValues, filterBy: MilestoneFilterStatusFlag.REJECTED });
          }}
          loading={statsLoading}
        />
        <StatBlockGridFEStyled
          data={plannedMilestoneStats}
          label={t`Planned`}
          subLabel={t`Committed`}
          loading={statsLoading}
        />
        <StatBlockGridFEStyled data={claimedMilestoneStats} label={t`Claimed`} loading={statsLoading} />
        <StatBlockGridFEStyled data={ApprovedMilestoneStats} label={t`Approved`} loading={statsLoading} />
      </FEDashboardStatStyled>
      {isWideScreen ? (
        <SectionListStyled>
          <WorkPackageContainerStyled>
            <DashBoardLeftSectionStyled>
              <WorkpackageList crewMember={crewMember} selectedOwnershipType={selectedOwnershipType} />
            </DashBoardLeftSectionStyled>
          </WorkPackageContainerStyled>
          <PendingApprovalListContainerStyled>
            <PendingApprovalList crewMember={crewMember} />
          </PendingApprovalListContainerStyled>
        </SectionListStyled>
      ) : (
        <Carousel>
          <WorkpackageList crewMember={crewMember} selectedOwnershipType={selectedOwnershipType} />
          <PendingApprovalList crewMember={crewMember} />
        </Carousel>
      )}
    </FEDashboardContentStyled>
  );
};
